import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Nav } from "../components/Nav";
import { api } from "../lib/api";
import { TimeSeriesChart, BreakdownBar, DonutBreakdown } from "../components/AnalyticsCharts";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "../components/ui/button";
import { ArrowLeft, Copy, Check, Download, ExternalLink } from "lucide-react";
import { API_URL } from "../lib/api";

export default function LinkDetail() {
    const { id } = useParams();
    const [link, setLink] = useState(null);
    const [analytics, setAnalytics] = useState(null);
    const [copied, setCopied] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const [{ data: l }, { data: a }] = await Promise.all([
                    api.get(`/links/${id}`),
                    api.get(`/analytics/link/${id}`),
                ]);
                setLink(l);
                setAnalytics(a);
            } catch (e) {
                setErr(e.response?.data?.detail || e.message);
            }
        })();
    }, [id]);

    if (err) {
        return (
            <div className="App min-h-screen">
                <Nav />
                <div className="mx-auto max-w-3xl px-6 py-16 text-center">
                    <div className="text-lg font-semibold text-destructive">{err}</div>
                    <Button asChild className="mt-6"><Link to="/dashboard">Back to dashboard</Link></Button>
                </div>
            </div>
        );
    }
    if (!link || !analytics) {
        return <div className="flex min-h-screen items-center justify-center font-mono text-sm text-muted-foreground">Loading…</div>;
    }

    const copy = async () => {
        await navigator.clipboard.writeText(link.short_url);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    return (
        <div className="App min-h-screen">
            <Nav />
            <div className="mx-auto max-w-7xl px-6 py-10">
                <Button asChild variant="ghost" size="sm" className="mb-6" data-testid="back-to-dashboard">
                    <Link to="/dashboard"><ArrowLeft className="mr-2 h-4 w-4" /> Back</Link>
                </Button>

                <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_320px]">
                    <div>
                        <div className="text-xs uppercase tracking-widest text-muted-foreground">Link</div>
                        <a href={link.short_url} target="_blank" rel="noreferrer"
                           className="mt-2 block break-all font-display text-4xl font-bold text-primary hover:underline"
                           data-testid="detail-short-url">
                            {link.short_url}
                        </a>
                        <div className="mt-3 truncate font-mono text-sm text-muted-foreground">
                            → {link.long_url}
                        </div>
                        <div className="mt-6 flex flex-wrap gap-2">
                            <Button onClick={copy} className="gap-2" data-testid="detail-copy-btn">
                                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                                {copied ? "Copied" : "Copy"}
                            </Button>
                            <Button variant="outline" asChild data-testid="detail-open-btn">
                                <a href={link.short_url} target="_blank" rel="noreferrer" className="gap-2">
                                    <ExternalLink className="h-4 w-4" /> Open
                                </a>
                            </Button>
                            <Button variant="outline" asChild data-testid="detail-png-btn">
                                <a href={`${API_URL}/qr/${link.slug}?fmt=png`} download className="gap-2">
                                    <Download className="h-4 w-4" /> PNG
                                </a>
                            </Button>
                            <Button variant="outline" asChild data-testid="detail-svg-btn">
                                <a href={`${API_URL}/qr/${link.slug}?fmt=svg`} download className="gap-2">
                                    <Download className="h-4 w-4" /> SVG
                                </a>
                            </Button>
                        </div>
                    </div>
                    <div className="border border-border bg-card p-6">
                        <div className="rounded-md bg-white p-3">
                            <QRCodeSVG value={link.short_url} size={220} fgColor="#0F172A" bgColor="#ffffff" />
                        </div>
                    </div>
                </div>

                <div className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-4">
                    <Metric label="Total clicks" value={analytics.total_clicks} testId="metric-total" />
                    <Metric label="Countries" value={analytics.by_country?.length || 0} testId="metric-countries" />
                    <Metric label="Devices" value={analytics.by_device?.length || 0} testId="metric-devices" />
                    <Metric label="Sources" value={analytics.by_referer?.length || 0} testId="metric-sources" />
                </div>

                <div className="mt-10">
                    <h2 className="mb-4 font-display text-xl font-bold">Clicks over time</h2>
                    <div className="border border-border bg-card p-4">
                        <TimeSeriesChart data={analytics.timeseries} />
                    </div>
                </div>

                <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                    <BreakdownBar data={analytics.by_country} title="Top countries" testId="chart-countries" />
                    <DonutBreakdown data={analytics.by_device} title="Devices" testId="chart-devices" />
                    <DonutBreakdown data={analytics.by_os} title="Operating systems" testId="chart-os" />
                    <BreakdownBar data={analytics.by_browser} title="Browsers" testId="chart-browsers" />
                    <BreakdownBar data={analytics.by_referer} title="Referrers" testId="chart-referrers" />
                </div>
            </div>
        </div>
    );
}

function Metric({ label, value, testId }) {
    return (
        <div className="border border-border bg-card p-5" data-testid={testId}>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
            <div className="mt-3 font-display text-3xl font-bold">{value}</div>
        </div>
    );
}
